/**
 * Agent hotel multi-room pricing UI — board per combo room.
 * When Gimmonix supplies per-room supplier prices: combo total = sum(per-room SSOT).
 * When only PackagePrice exists: HR runs once on the combination total; room lines are display splits.
 */

import { getHotelSummaryLegs, normalizeMultiRoomProfileSelection } from "../getHotelSummaryLegs";
import { formatHotelIlsRoundedDisplay } from "../formatHotelIlsRoundedDisplay";
import { formatRoomNameEntry } from "../formatRoomNameEntry";
import { getLdsBoardBasisLabelHebrew } from "../ldsBoardBasisHebrew";
import { pickFirstNonEmptyTrimmed } from "../orders/hotelBoardBasis";
import { getHotelProfilePackageSupplierUsd } from "../hotelSellingPrice";
import { roundMoney2 } from "../../pricing/pricingEngine";
import {
  isAgentHotelCustomerFacingPricingEnabled,
  resolveAgentHotelCustomerFacingPriceIls,
} from "./resolveAgentHotelCustomerFacingPriceIls";

function isGimmonixMultiRoomPriceDebugEnabled() {
  try {
    if (
      typeof globalThis !== "undefined" &&
      globalThis?.process?.env?.HOTEL_GIMMONIX_MULTIROOM_PRICE_DEBUG === "1"
    ) {
      return true;
    }
  } catch {
    /* ignore */
  }
  try {
    if (typeof import.meta !== "undefined") {
      if (import.meta?.env?.VITE_HOTEL_GIMMONIX_MULTIROOM_PRICE_DEBUG === "1") {
        return true;
      }
      if (import.meta?.env?.HOTEL_GIMMONIX_MULTIROOM_PRICE_DEBUG === "1") {
        return true;
      }
    }
  } catch {
    /* ignore */
  }
  return false;
}

function emitGimmonixMultiRoomPriceDebug(payload) {
  if (!isGimmonixMultiRoomPriceDebugEnabled()) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[HOTEL_GIMMONIX_MULTIROOM_PRICE_DEBUG]", payload);
  } catch {
    /* ignore */
  }
}

function resolveRoomQuoteForLeg(order, leg) {
  const rq = order?.compositeBookingContext?.room_quotes;
  if (!Array.isArray(rq)) return null;
  const ri = Number(leg.room_index ?? leg.roomIndex - 1);
  return (
    rq.find((q) => Number(q?.room_index ?? -1) === ri) ?? rq[leg.roomIndex - 1] ?? null
  );
}

function resolvePackageForLeg(order, leg, source) {
  if (source === "multiRoomProfileSelections") {
    const mrs = order?.multiRoomProfileSelections;
    if (!Array.isArray(mrs)) return null;
    const ri = Number(leg.room_index ?? leg.roomIndex - 1);
    const sel =
      mrs.find((s) => Number(s?.room_index ?? -1) === ri) ?? mrs[leg.roomIndex - 1];
    const norm = normalizeMultiRoomProfileSelection(sel);
    return norm?.selected_option?.selectedHotelPackage ?? null;
  }
  const q = resolveRoomQuoteForLeg(order, leg);
  return q?.package_row ?? null;
}

/** Board from each combo room object (gemonix room / package_row), not first room only. */
export function resolveComboRoomBoardBasis(comboRoomQuote, pkg) {
  const g0 =
    comboRoomQuote?.gemonix_rooms?.[0] ?? comboRoomQuote?.package_row?.Rooms?.[0] ?? null;
  return pickFirstNonEmptyTrimmed([
    g0?.RoomBasis,
    g0?.roomBasis,
    g0?.Board,
    g0?.board,
    pkg?.RoomBasis,
    pkg?.roomBasis,
    pkg?.BoardName,
    pkg?.boardName,
    pkg?.board,
  ]);
}

export function formatComboRoomBoardLabel(raw) {
  if (!raw) return "—";
  const label = getLdsBoardBasisLabelHebrew(raw);
  return label || String(raw).trim() || "—";
}

/**
 * Per-room customer-facing price — same SSOT as single-room profile (margin on room supplier only).
 * @returns {{
 *   supplierRoomPrice: number|null,
 *   roomMarginApplied: number|null,
 *   customerFacingRoomPrice: number|null,
 *   pricingStatus: string,
 *   pipeline: string|null,
 * }}
 */
export function resolveAgentHotelMultiRoomLegCustomerFacingSsot(
  pkg,
  profilePriceContext = null
) {
  const baseFail = {
    supplierRoomPrice: null,
    roomMarginApplied: null,
    customerFacingRoomPrice: null,
    pricingStatus: "not_agent_hr",
    pipeline: null,
  };

  if (!pkg) {
    return { ...baseFail, pricingStatus: "missing_package" };
  }

  const supplierRoomPrice = getHotelProfilePackageSupplierUsd(pkg);
  if (supplierRoomPrice == null || !Number.isFinite(Number(supplierRoomPrice))) {
    return { ...baseFail, pricingStatus: "missing_supplier" };
  }

  if (!isAgentHotelCustomerFacingPricingEnabled(profilePriceContext)) {
    return {
      ...baseFail,
      supplierRoomPrice: Number(supplierRoomPrice),
      customerFacingRoomPrice: Number(supplierRoomPrice),
      pricingStatus: "legacy_supplier_only",
    };
  }

  const cf = resolveAgentHotelCustomerFacingPriceIls({
    pkg,
    hotelInfo: profilePriceContext?.hotelInfo ?? null,
    profilePriceContext,
    pricingModel: "agent_hr_multi_room_pricing_leg",
  });

  if (cf.pricingStatus !== "ok" || cf.bookingSsotIls == null) {
    return {
      supplierRoomPrice: Number(supplierRoomPrice),
      roomMarginApplied: cf.hrCommission ?? null,
      customerFacingRoomPrice: null,
      pricingStatus: cf.pricingStatus ?? "agent_hr_reject",
      pipeline: cf.pipeline ?? null,
    };
  }

  return {
    supplierRoomPrice: cf.supplierNet ?? Number(supplierRoomPrice),
    roomMarginApplied: cf.hrCommission ?? null,
    customerFacingRoomPrice: cf.bookingSsotIls,
    pricingStatus: "ok",
    pipeline: cf.pipeline ?? "agent_hr_customer_facing_ssot",
  };
}

/**
 * What the combo total would be if HR ran once on combined supplier (incorrect — trace only).
 */
export function resolveMisappliedCombinedSupplierComboTotal(
  combinedSupplierAmount,
  profilePriceContext = null
) {
  const supplier = Number(combinedSupplierAmount);
  if (!Number.isFinite(supplier) || supplier <= 0) return null;
  if (!isAgentHotelCustomerFacingPricingEnabled(profilePriceContext)) {
    return supplier;
  }
  const cf = resolveAgentHotelCustomerFacingPriceIls({
    supplierAmount: supplier,
    supplierCurrency:
      profilePriceContext?.selectedHotelCurrency ??
      profilePriceContext?.hotelSearchListDisplayCurrency ??
      "ILS",
    profilePriceContext,
    pricingModel: "agent_hr_multi_room_misapplied_combined_supplier",
  });
  return cf.pricingStatus === "ok" ? cf.bookingSsotIls : null;
}

function formatRoomPricingLine({ roomIndex, roomName, boardLabel, priceDisplay }) {
  const parts = [`חדר ${roomIndex}: ${roomName}`, `בסיס אירוח: ${boardLabel}`];
  if (priceDisplay) parts.push(`מחיר: ${priceDisplay}`);
  return parts.join(" — ");
}

/** Search Offer / checkout sidebar — room name, board, price without profile labels. */
export function formatSearchOfferMultiRoomPriceLine(row) {
  if (!row || typeof row !== "object") return "";
  const parts = [`חדר ${row.roomIndex}: ${row.roomName}`];
  if (row.boardLabel && row.boardLabel !== "—") {
    parts.push(row.boardLabel);
  }
  if (row.priceDisplay) {
    parts.push(row.priceDisplay);
  }
  return parts.join(" — ");
}

export function resolveSearchOfferShowPerRoomPricing(order = null, display = null) {
  const roomsCount = resolveSearchOfferRoomsCount(order);
  const multiRoomProfileSelectionsLength = Array.isArray(
    order?.multiRoomProfileSelections
  )
    ? order.multiRoomProfileSelections.length
    : 0;
  return (
    roomsCount >= 2 ||
    multiRoomProfileSelectionsLength >= 2 ||
    display?.showPerRoomPricing === true
  );
}

export function shouldShowSearchOfferMultiRoomPriceBreakdown(display, order = null) {
  const showPerRoomPricing = resolveSearchOfferShowPerRoomPricing(order, display);

  return Boolean(
    showPerRoomPricing &&
      Array.isArray(display?.roomRows) &&
      display.roomRows.length >= 2 &&
      display?.totalIls != null
  );
}

/** Rooms count for searchoffer summary — not gated on board differences. */
export function resolveSearchOfferRoomsCount(order) {
  if (!order || typeof order !== "object") return 0;

  const multiSel = order.multiRoomProfileSelections;
  if (Array.isArray(multiSel) && multiSel.length >= 2) {
    return multiSel.length;
  }

  const pkgRooms = normalizePkgRoomsArray(order.selectedHotelPackage);
  if (pkgRooms.length >= 2) return pkgRooms.length;

  const roomNames = order.selectedRoom?.roomNames;
  if (Array.isArray(roomNames) && roomNames.length >= 2) {
    return roomNames.length;
  }

  const searchRooms = order.searchPhrase?.rooms;
  if (Array.isArray(searchRooms) && searchRooms.length >= 2) {
    return searchRooms.length;
  }

  const rq = order.compositeBookingContext?.room_quotes;
  if (Array.isArray(rq) && rq.length >= 2) return rq.length;

  const legsModel = getHotelSummaryLegs(order, { t: (k) => k });
  if (legsModel.isMultiRoom && legsModel.legs.length >= 2) {
    return legsModel.legs.length;
  }

  return 0;
}

export const SEARCH_OFFER_SUMMARY_RENDER_TRACE =
  "[SEARCH_OFFER_SUMMARY_RENDER_TRACE]";

export function emitSearchOfferSummaryRenderTrace(payload) {
  try {
    // eslint-disable-next-line no-console
    console.info(SEARCH_OFFER_SUMMARY_RENDER_TRACE, {
      ts: Date.now(),
      ...payload,
    });
  } catch {
    /* ignore */
  }
}

function normalizePkgRoomsArray(pkg) {
  const rooms = pkg?.Rooms;
  if (Array.isArray(rooms)) return rooms;
  if (rooms && typeof rooms === "object") return Object.values(rooms);
  return [];
}

function resolveRoomEntrySupplierUsd(roomEntry) {
  if (!roomEntry || typeof roomEntry !== "object") return null;
  const n = Number(
    roomEntry?.Price?.FinalPrice ??
      roomEntry?.price?.FinalPrice ??
      roomEntry?.FinalPrice ??
      roomEntry?.finalPrice
  );
  return Number.isFinite(n) && n > 0 ? roundMoney2(n) : null;
}

/** True when every leg already carries an explicit supplier room price from Gemonix. */
function packageHasExplicitPerRoomSupplierPrices(pkg) {
  const rooms = normalizePkgRoomsArray(pkg);
  if (rooms.length < 2) return false;
  return rooms.every((entry) => resolveRoomEntrySupplierUsd(entry) != null);
}

function resolvePackagePriceFinalUsd(pkg) {
  const n = Number(
    pkg?.PackagePrice?.FinalPrice ??
      pkg?.PackagePrice?.finalPrice ??
      pkg?.packagePrice?.FinalPrice ??
      pkg?.packagePrice?.finalPrice
  );
  return Number.isFinite(n) && n > 0 ? roundMoney2(n) : null;
}

function evaluateGimmonixSupplierTotalRule(pkg) {
  const rooms = normalizePkgRoomsArray(pkg);
  const roomPrices = rooms.map((r) => resolveRoomEntrySupplierUsd(r));
  const hasAllRoomPrices = rooms.length >= 2 && roomPrices.every((n) => n != null);
  const packageFinal = resolvePackagePriceFinalUsd(pkg);
  const sumRooms = hasAllRoomPrices
    ? roundMoney2(roomPrices.reduce((sum, n) => sum + Number(n), 0))
    : null;
  const tolerance = packageFinal != null ? Math.max(0.01, (packageFinal * 0.5) / 100) : null;
  const packageEqualsSumRooms =
    packageFinal != null &&
    sumRooms != null &&
    tolerance != null &&
    Math.abs(Number(packageFinal) - Number(sumRooms)) <= tolerance;
  const packageEqualsOneRoom =
    packageFinal != null &&
    hasAllRoomPrices &&
    roomPrices.some((n) => Math.abs(Number(n) - Number(packageFinal)) <= 0.01);

  if (hasAllRoomPrices && packageEqualsSumRooms) {
    return {
      branch: "package_price",
      chosenSupplierTotalUsd: packageFinal,
      roomSupplierUsd: roomPrices,
      packageFinal,
      sumRooms,
      packageEqualsSumRooms: true,
      hasAllRoomPrices,
    };
  }
  if (
    hasAllRoomPrices &&
    sumRooms != null &&
    (packageEqualsOneRoom || packageFinal == null)
  ) {
    return {
      branch: "gimmonix_rooms_sum_total",
      chosenSupplierTotalUsd: sumRooms,
      roomSupplierUsd: roomPrices,
      packageFinal,
      sumRooms,
      packageEqualsSumRooms: false,
      hasAllRoomPrices,
    };
  }
  if (!hasAllRoomPrices && packageFinal != null) {
    return {
      branch: "gimmonix_package_combo_total",
      chosenSupplierTotalUsd: packageFinal,
      roomSupplierUsd: roomPrices,
      packageFinal,
      sumRooms,
      packageEqualsSumRooms: null,
      hasAllRoomPrices,
    };
  }
  return {
    branch: "validation_fail",
    chosenSupplierTotalUsd: null,
    roomSupplierUsd: roomPrices,
    packageFinal,
    sumRooms,
    packageEqualsSumRooms,
    hasAllRoomPrices,
  };
}

/** Hotel profile combo offer — 2+ Rooms in package or SimilarRooms with 2+ room names. */
export function isHotelProfileMultiRoomComboPackage(room, pkg, order = null) {
  const roomsArr = normalizePkgRoomsArray(pkg);
  if (roomsArr.length >= 2) return true;

  const snapRooms = normalizePkgRoomsArray(order?.selectedPackageOriginalSnapshot);
  if (snapRooms.length >= 2) return true;

  const pkgRoomNames = pkg?.roomNames;
  if (Array.isArray(pkgRoomNames) && pkgRoomNames.length >= 2) return true;

  if (
    room?.SimilarRooms &&
    Array.isArray(room?.roomNames) &&
    room.roomNames.length >= 2
  ) {
    return true;
  }

  if (order && typeof order === "object") {
    const searchRooms = order.searchPhrase?.rooms;
    if (Array.isArray(searchRooms) && searchRooms.length >= 2) return true;
    const multiSel = order.multiRoomProfileSelections;
    if (Array.isArray(multiSel) && multiSel.length >= 2) return true;
  }

  return false;
}

function resolveProfileLegCountFromSources(room, pkg, order = null) {
  const roomsArr = normalizePkgRoomsArray(pkg);
  const snapRooms = normalizePkgRoomsArray(order?.selectedPackageOriginalSnapshot);
  const pkgRoomNames = Array.isArray(pkg?.roomNames) ? pkg.roomNames : [];
  const roomNames = Array.isArray(room?.roomNames) ? room.roomNames : [];
  const searchRooms = order?.searchPhrase?.rooms;
  const multiSel = order?.multiRoomProfileSelections;

  return Math.max(
    roomsArr.length >= 2 ? roomsArr.length : 0,
    snapRooms.length >= 2 ? snapRooms.length : 0,
    room?.SimilarRooms && roomNames.length >= 2 ? roomNames.length : 0,
    pkgRoomNames.length >= 2 ? pkgRoomNames.length : 0,
    Array.isArray(searchRooms) && searchRooms.length >= 2 ? searchRooms.length : 0,
    Array.isArray(multiSel) && multiSel.length >= 2 ? multiSel.length : 0
  );
}

/** Restore per-room Rooms[] on draft pkg from snapshot or split combined supplier. */
export function enrichComboPackageWithRoomsForPricing(order, room, pkg) {
  if (!pkg || typeof pkg !== "object") return pkg;

  const existing = normalizePkgRoomsArray(pkg);
  const legCount = resolveProfileLegCountFromSources(room, pkg, order);
  if (legCount < 2) return pkg;

  const snapRooms = normalizePkgRoomsArray(order?.selectedPackageOriginalSnapshot);
  if (existing.length < 2 && snapRooms.length >= 2) {
    return { ...pkg, Rooms: snapRooms };
  }

  const combinedSupplier = getHotelProfilePackageSupplierUsd(pkg);
  const hasCombinedSupplier =
    combinedSupplier != null &&
    Number.isFinite(Number(combinedSupplier)) &&
    Number(combinedSupplier) > 0;

  if (existing.length >= 2) {
    const pkgCurrency =
      pkg?.PackagePrice?.Currency ??
      pkg?.PriceCurrency ??
      pkg?.priceCurrency ??
      "ILS";

    const missingIndexes = [];
    let knownSum = 0;
    for (let i = 0; i < existing.length; i += 1) {
      const n = Number(
        existing[i]?.Price?.FinalPrice ??
          existing[i]?.price?.FinalPrice ??
          existing[i]?.FinalPrice ??
          existing[i]?.finalPrice
      );
      if (Number.isFinite(n) && n > 0) {
        knownSum = roundMoney2(knownSum + n);
      } else {
        missingIndexes.push(i);
      }
    }
    if (!missingIndexes.length || !hasCombinedSupplier) return pkg;

    const combined = Number(combinedSupplier);
    const remaining = roundMoney2(combined - knownSum);
    const fallbackPerMissing = roundMoney2(combined / existing.length);
    const basePerMissing =
      remaining > 0 ? roundMoney2(remaining / missingIndexes.length) : fallbackPerMissing;

    let runningPatched = 0;
    const patchedRooms = existing.map((entry, idx) => {
      if (!missingIndexes.includes(idx)) return entry;
      const isLastMissing = idx === missingIndexes[missingIndexes.length - 1];
      const missingCount = missingIndexes.length;
      const target =
        isLastMissing
          ? roundMoney2(
              (remaining > 0 ? remaining : roundMoney2(fallbackPerMissing * missingCount)) -
                runningPatched
            )
          : basePerMissing;
      runningPatched = roundMoney2(runningPatched + target);
      return {
        ...entry,
        Price: {
          ...(entry?.Price ?? entry?.price ?? {}),
          FinalPrice: target,
          Currency: entry?.Price?.Currency ?? entry?.price?.Currency ?? pkgCurrency,
          SupplierCurrency:
            entry?.Price?.SupplierCurrency ?? entry?.price?.SupplierCurrency ?? pkgCurrency,
        },
      };
    });

    emitGimmonixMultiRoomPriceDebug({
      stage: "enrichComboPackageWithRoomsForPricing",
      packageId: pkg?.packageID ?? pkg?.PackageId ?? null,
      roomCount: existing.length,
      supplierTotal: combined,
      knownRoomPricesSum: knownSum,
      missingRoomIndexes: missingIndexes,
      normalizedRoomPrices: patchedRooms.map(
        (r) => r?.Price?.FinalPrice ?? r?.price?.FinalPrice ?? null
      ),
      strategy: "fill_missing_room_prices_from_combined_total",
    });

    return { ...pkg, Rooms: patchedRooms };
  }

  const perLegSupplier =
    hasCombinedSupplier
      ? roundMoney2(Number(combinedSupplier) / legCount)
      : null;
  const board =
    pkg?.roomBasis ?? pkg?.RoomBasis ?? pkg?.BoardName ?? pkg?.boardName ?? null;

  const synthesized = [];
  for (let i = 0; i < legCount; i += 1) {
    const snapEntry = snapRooms[i] ?? null;
    synthesized.push({
      ...(snapEntry && typeof snapEntry === "object" ? snapEntry : {}),
      RoomName: resolveProfileLegRoomName(room, snapRooms.length ? snapRooms : existing, i, pkg),
      RoomBasis:
        snapEntry?.RoomBasis ??
        snapEntry?.roomBasis ??
        board,
      Price:
        snapEntry?.Price ??
        snapEntry?.price ??
        (perLegSupplier != null ? { FinalPrice: perLegSupplier } : undefined),
    });
  }

  return { ...pkg, Rooms: synthesized };
}

function buildSearchOfferBookingSsotSplitRoomRows(order, profilePriceContext) {
  const legCount = resolveSearchOfferRoomsCount(order);
  if (legCount < 2) return null;

  const totalIls =
    resolveStoredOrderBookingSsotIls(order) ??
    (Number.isFinite(Number(order?.displayedPriceAtClick))
      ? roundMoney2(Number(order.displayedPriceAtClick))
      : null);
  if (totalIls == null) return null;

  const room = order?.selectedRoom ?? null;
  const pkg = order?.selectedHotelPackage ?? null;
  const enrichedPkg = enrichComboPackageWithRoomsForPricing(order, room, pkg);
  const roomsArr = normalizePkgRoomsArray(enrichedPkg);
  const perRoomBase = roundMoney2(totalIls / legCount);
  const boardRaw = pkg?.roomBasis ?? pkg?.RoomBasis ?? null;
  const boardLabel = formatComboRoomBoardLabel(boardRaw);

  const roomRows = [];
  let runningSum = 0;
  for (let i = 0; i < legCount; i += 1) {
    const isLast = i === legCount - 1;
    const priceIls = isLast ? roundMoney2(totalIls - runningSum) : perRoomBase;
    runningSum = roundMoney2(runningSum + priceIls);
    const roomName = resolveProfileLegRoomName(room, roomsArr, i, enrichedPkg);
    const priceDisplay =
      priceIls != null ? formatHotelIlsRoundedDisplay(priceIls) : null;
    roomRows.push({
      roomIndex: i + 1,
      roomName,
      boardRaw,
      boardLabel,
      priceIls,
      priceDisplay,
      line: formatRoomPricingLine({
        roomIndex: i + 1,
        roomName,
        boardLabel,
        priceDisplay,
      }),
      trace: {
        supplierRoomPrice: null,
        roomMarginApplied: null,
        customerFacingRoomPrice: priceIls,
        pricingStatus: "searchoffer_booking_ssot_split",
        pipeline: "searchoffer_booking_ssot_split",
      },
    });
  }

  return {
    ...finalizeMultiRoomDisplayFromRoomRows(roomRows, profilePriceContext, {
      source: "searchoffer_booking_ssot_split",
    }),
    source: "searchoffer_booking_ssot_split",
  };
}

function resolveProfileComboMultiRoomDisplay(order, profilePriceContext) {
  if (
    !isHotelProfileMultiRoomComboPackage(
      order?.selectedRoom,
      order?.selectedHotelPackage,
      order
    )
  ) {
    return null;
  }

  const enrichedPkg = enrichComboPackageWithRoomsForPricing(
    order,
    order?.selectedRoom,
    order?.selectedHotelPackage
  );
  const profileDisplay = buildAgentHotelProfilePackageMultiRoomPricingDisplay({
    room: order?.selectedRoom,
    pkg: enrichedPkg,
    profilePriceContext,
  });

  if (
    profileDisplay.isMultiRoom &&
    Array.isArray(profileDisplay.roomRows) &&
    profileDisplay.roomRows.length >= 2
  ) {
    return {
      ...profileDisplay,
      showPerRoomPricing: true,
      source: profileDisplay.source ?? "hotel_profile_combo_package",
    };
  }

  return profileDisplay;
}

function resolveProfileLegRoomName(room, roomsArr, index, pkg = null) {
  const fromPkgNames = pkg?.roomNames?.[index];
  if (fromPkgNames != null) {
    const formatted = formatRoomNameEntry(fromPkgNames);
    if (formatted.trim()) return formatted.trim();
  }

  const fromNames = room?.roomNames?.[index];
  if (fromNames != null) {
    const formatted = formatRoomNameEntry(fromNames);
    if (formatted.trim()) return formatted.trim();
  }
  const entry = roomsArr[index];
  const fromEntry = entry?.RoomName ?? entry?.roomName ?? entry?.Name ?? entry?.name;
  if (fromEntry != null && String(fromEntry).trim()) {
    return String(fromEntry).trim();
  }
  return `חדר ${index + 1}`;
}

function buildProfileLegSyntheticPackage(parentPkg, roomEntry) {
  const supplier = resolveRoomEntrySupplierUsd(roomEntry);
  const parentCur =
    parentPkg?.PackagePrice?.Currency ??
    parentPkg?.PriceCurrency ??
    parentPkg?.priceCurrency ??
    "ILS";
  const board =
    roomEntry?.RoomBasis ??
    roomEntry?.roomBasis ??
    parentPkg?.roomBasis ??
    parentPkg?.RoomBasis;
  const legIdentity = {
    packageID: parentPkg?.packageID ?? parentPkg?.PackageId ?? parentPkg?.packageId,
    priceCurrency: parentCur,
    supplierCurrency: parentPkg?.supplierCurrency,
    roomBasis: board,
    RoomBasis: board,
  };

  if (supplier == null) {
    return legIdentity;
  }

  return {
    ...legIdentity,
    PackagePrice: { FinalPrice: supplier, Currency: parentCur },
    finalPrice: supplier,
    FinalPrice: supplier,
  };
}

function buildPackageComboOnceDisplayRoomRows({
  room,
  enrichedPkg,
  roomsArr,
  legCount,
  comboBookingSsotIls,
  supplierRoomUsd = [],
}) {
  const totalIls = roundMoney2(comboBookingSsotIls);
  const perRoomBase = roundMoney2(totalIls / legCount);
  const roomRows = [];
  let runningSum = 0;

  for (let i = 0; i < legCount; i += 1) {
    const roomEntry = roomsArr[i] ?? null;
    const isLast = i === legCount - 1;
    const priceIls = isLast ? roundMoney2(totalIls - runningSum) : perRoomBase;
    runningSum = roundMoney2(runningSum + priceIls);
    const roomName = resolveProfileLegRoomName(room, roomsArr, i, enrichedPkg);
    const boardRaw =
      resolveComboRoomBoardBasis(
        roomEntry ? { gemonix_rooms: [roomEntry], package_row: { Rooms: [roomEntry] } } : null,
        enrichedPkg
      ) ?? null;
    const boardLabel = formatComboRoomBoardLabel(boardRaw);
    const priceDisplay = formatHotelIlsRoundedDisplay(priceIls);
    roomRows.push({
      roomIndex: i + 1,
      roomName,
      boardRaw,
      boardLabel,
      priceIls,
      priceDisplay,
      line: formatRoomPricingLine({
        roomIndex: i + 1,
        roomName,
        boardLabel,
        priceDisplay,
      }),
      trace: {
        supplierRoomPrice:
          Number.isFinite(Number(supplierRoomUsd[i])) && Number(supplierRoomUsd[i]) > 0
            ? Number(supplierRoomUsd[i])
            : resolveRoomEntrySupplierUsd(roomEntry),
        roomMarginApplied: null,
        customerFacingRoomPrice: priceIls,
        pricingStatus: "combo_package_once_display_split",
        pipeline: "agent_hr_combo_package_once_display_split",
      },
    });
  }

  return roomRows;
}

function finalizeMultiRoomDisplayFromRoomRows(roomRows, profilePriceContext, extras = {}) {
  const uniqueBoards = [
    ...new Set(roomRows.map((r) => r.boardLabel).filter((b) => b && b !== "—")),
  ];
  const showPerRoomBoard = uniqueBoards.length > 1;
  const sharedBoardLabel = uniqueBoards.length === 1 ? uniqueBoards[0] : null;

  const perRoomPrices = roomRows
    .map((r) => Number(r.priceIls))
    .filter((n) => Number.isFinite(n) && n > 0);
  const comboTotal =
    perRoomPrices.length === roomRows.length && perRoomPrices.length > 0
      ? roundMoney2(perRoomPrices.reduce((acc, n) => acc + n, 0))
      : null;

  const combinedSupplierTotal = roundMoney2(
    roomRows.reduce(
      (acc, r) =>
        acc +
        (Number(r.trace?.supplierRoomPrice) > 0 ? Number(r.trace.supplierRoomPrice) : 0),
      0
    )
  );
  const misappliedComboTotal = resolveMisappliedCombinedSupplierComboTotal(
    combinedSupplierTotal,
    profilePriceContext
  );

  const totalIls = comboTotal;
  const totalDisplay = totalIls != null ? formatHotelIlsRoundedDisplay(totalIls) : null;

  const trace = {
    supplierRoomPrice: roomRows.map((r) => r.trace?.supplierRoomPrice ?? null),
    roomMarginApplied: roomRows.map((r) => r.trace?.roomMarginApplied ?? null),
    customerFacingRoomPrice: roomRows.map((r) => r.trace?.customerFacingRoomPrice ?? null),
    comboTotal: totalIls,
    combinedSupplierTotal,
    misappliedComboTotal,
    comboTotalEqualsPerRoomSum:
      comboTotal != null &&
      perRoomPrices.reduce((a, b) => a + b, 0) === comboTotal,
    perRoomMarginAppliedIndependently:
      misappliedComboTotal == null || comboTotal == null
        ? null
        : Math.abs(comboTotal - misappliedComboTotal) > 0.01,
    ...extras,
  };

  emitGimmonixMultiRoomPriceDebug({
    stage: "finalizeMultiRoomDisplayFromRoomRows",
    roomCount: roomRows.length,
    supplierTotal: combinedSupplierTotal,
    normalizedRoomPrices: roomRows.map((r) => r.trace?.supplierRoomPrice ?? null),
    customerFacingRoomPrices: roomRows.map((r) => r.priceIls ?? null),
    finalDisplayedTotal: totalIls,
    source: extras?.source ?? null,
  });

  return {
    isMultiRoom: true,
    roomRows,
    showPerRoomBoard,
    showPerRoomPricing: roomRows.length >= 2,
    sharedBoardLabel,
    totalIls,
    totalDisplay,
    totalLine: totalDisplay ? `סה״כ לתשלום: ${totalDisplay}` : null,
    pipeline: "agent_hr_customer_facing_ssot",
    trace,
  };
}

/**
 * Hotel profile room card / offer row — per-room SSOT from pkg.Rooms[], not combined PackagePrice.
 * @param {{ room: object|null, pkg: object|null, profilePriceContext?: object|null, order?: object|null }} input
 */
export function buildAgentHotelProfilePackageMultiRoomPricingDisplay({
  room,
  pkg,
  profilePriceContext = null,
  order = null,
}) {
  const empty = {
    isMultiRoom: false,
    roomRows: [],
    showPerRoomBoard: false,
    showPerRoomPricing: false,
    sharedBoardLabel: null,
    totalIls: null,
    totalDisplay: null,
    totalLine: null,
    source: "hotel_profile_combo_package",
    pipeline: null,
    trace: null,
  };

  if (!isHotelProfileMultiRoomComboPackage(room, pkg, order)) {
    return empty;
  }

  const supplierRule = evaluateGimmonixSupplierTotalRule(pkg);
  const enrichedPkg = enrichComboPackageWithRoomsForPricing(order, room, pkg);
  const roomsArr = normalizePkgRoomsArray(enrichedPkg);
  const legCount = Math.max(
    roomsArr.length >= 2 ? roomsArr.length : 0,
    room?.SimilarRooms && Array.isArray(room?.roomNames) ? room.roomNames.length : 0,
    Array.isArray(pkg?.roomNames) && pkg.roomNames.length >= 2 ? pkg.roomNames.length : 0
  );
  if (legCount < 2) {
    return empty;
  }

  if (supplierRule.branch === "validation_fail") {
    return {
      ...empty,
      trace: {
        pricingStatus: "validation_fail",
        supplierRule,
        sourceBranch: "validation_fail",
      },
    };
  }

  if (
    supplierRule.branch === "package_price" ||
    supplierRule.branch === "gimmonix_rooms_sum_total" ||
    supplierRule.branch === "gimmonix_package_combo_total"
  ) {
    const chosenSupplierTotalSource =
      supplierRule.branch === "gimmonix_rooms_sum_total"
        ? "rooms_sum"
        : supplierRule.branch === "gimmonix_package_combo_total"
          ? "combo_package_price"
          : "package_price";
    const comboCf = resolveAgentHotelCustomerFacingPriceIls({
      supplierAmount: supplierRule.chosenSupplierTotalUsd,
      supplierCurrency:
        pkg?.PackagePrice?.Currency ??
        pkg?.priceCurrency ??
        pkg?.PriceCurrency ??
        "USD",
      profilePriceContext,
      pricingModel: "agent_hr_multi_room_combo_package_once",
      supplierDecision: {
        chosenSupplierTotalSource,
        chosenSupplierTotalUsd: supplierRule.chosenSupplierTotalUsd,
        supplierCurrency:
          pkg?.PackagePrice?.Currency ??
          pkg?.priceCurrency ??
          pkg?.PriceCurrency ??
          "USD",
      },
    });
    if (comboCf.pricingStatus !== "ok" || comboCf.bookingSsotIls == null) {
      return {
        ...empty,
        trace: { pricingStatus: comboCf.pricingStatus ?? "combo_package_once_failed" },
      };
    }

    const combinedSupplier = getHotelProfilePackageSupplierUsd(pkg);
    const roomRows = buildPackageComboOnceDisplayRoomRows({
      room,
      enrichedPkg,
      roomsArr,
      legCount,
      comboBookingSsotIls: comboCf.bookingSsotIls,
      supplierRoomUsd: supplierRule.roomSupplierUsd,
    });

    return {
      ...finalizeMultiRoomDisplayFromRoomRows(roomRows, profilePriceContext, {
        source:
          supplierRule.branch === "gimmonix_rooms_sum_total"
            ? "gimmonix_rooms_sum_total"
            : "hotel_profile_combo_package",
        pricingStrategy: "combo_package_once",
        sourceBranch: supplierRule.branch,
        chosenSupplierTotalUsd: supplierRule.chosenSupplierTotalUsd,
        packageFinalPriceUsd: supplierRule.packageFinal,
        sumRoomsPriceUsd: supplierRule.sumRooms,
        packageEqualsSumRooms: supplierRule.packageEqualsSumRooms,
        chosenSupplierTotalSource,
        supplierNetIlsInput: comboCf.supplierNetIlsInput ?? null,
        supplierNetIlsOutput: comboCf.supplierNetIlsOutput ?? comboCf.supplierNet ?? null,
        usedDisplayCurrenciesIls: comboCf.usedDisplayCurrenciesIls ?? false,
        usedPackagePriceFinalPrice: comboCf.usedPackagePriceFinalPrice ?? false,
        usedRoomsSumFinalPrice: comboCf.usedRoomsSumFinalPrice ?? false,
        fallbackReason: comboCf.fallbackReason ?? null,
        misappliedCombinedPackageTotal:
          combinedSupplier != null
            ? resolveMisappliedCombinedSupplierComboTotal(
                roundMoney2(combinedSupplier * legCount),
                profilePriceContext
              )
            : null,
      }),
      source:
        supplierRule.branch === "gimmonix_rooms_sum_total"
          ? "gimmonix_rooms_sum_total"
          : "hotel_profile_combo_package",
      ourPrice: comboCf.ourPrice ?? null,
      packageCustomerTotalIls: comboCf.bookingSsotIls ?? null,
    };
  }
  return { ...empty, trace: { pricingStatus: "unsupported_supplier_rule", supplierRule } };
}

/**
 * Package-level booking SSOT for multi-room combo offers — never per-leg supplier fallback.
 * @returns {{ bookingSsotIls: number, trace: object, agentOurPrice: object|null }|null}
 */
export function resolveHotelProfileMultiRoomComboBookingSsot({
  room,
  pkg,
  profilePriceContext = null,
  order = null,
}) {
  if (!isHotelProfileMultiRoomComboPackage(room, pkg, order)) return null;

  const display = buildAgentHotelProfilePackageMultiRoomPricingDisplay({
    room,
    pkg,
    profilePriceContext,
    order,
  });
  if (
    !display?.isMultiRoom ||
    display.totalIls == null ||
    !Number.isFinite(Number(display.totalIls)) ||
    Number(display.totalIls) <= 0
  ) {
    return null;
  }

  const bookingSsotIls = roundMoney2(Number(display.totalIls));
  return {
    bookingSsotIls,
    trace: {
      pipeline: "agent_hr_multi_room_combo_ssot",
      source: display.source ?? "hotel_profile_combo_package",
      pricingStatus: "ok",
      chosenSupplierTotalSource: display.trace?.chosenSupplierTotalSource ?? null,
      chosenSupplierTotalUsd: display.trace?.chosenSupplierTotalUsd ?? null,
      supplierNetIlsInput: display.trace?.supplierNetIlsInput ?? null,
      supplierNetIlsOutput: display.trace?.supplierNetIlsOutput ?? null,
      usedDisplayCurrenciesIls: display.trace?.usedDisplayCurrenciesIls ?? false,
      usedPackagePriceFinalPrice: display.trace?.usedPackagePriceFinalPrice ?? false,
      usedRoomsSumFinalPrice: display.trace?.usedRoomsSumFinalPrice ?? false,
      fallbackReason: display.trace?.fallbackReason ?? null,
      sourceBranch: display.trace?.sourceBranch ?? null,
      supplierNet: display.trace?.supplierNetIlsOutput ?? null,
      packageCustomerTotalIls: display.packageCustomerTotalIls ?? bookingSsotIls,
      comboTotal: bookingSsotIls,
    },
    agentOurPrice: display.ourPrice ?? null,
  };
}

function resolveStoredOrderBookingSsotIls(order) {
  const raw =
    order?.bookingSsotIls ?? order?.selectedPriceSnapshot?.finalPriceIls ?? null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? roundMoney2(n) : null;
}

function buildMultiRoomLegRowsFromOrder(order, legsModel, profilePriceContext) {
  return legsModel.legs.map((leg) => {
    const roomQuote = resolveRoomQuoteForLeg(order, leg);
    const pkg = resolvePackageForLeg(order, leg, legsModel.source);
    const boardRaw =
      resolveComboRoomBoardBasis(roomQuote, pkg) ?? leg.roomBasis ?? null;
    const boardLabel = formatComboRoomBoardLabel(boardRaw);
    const legSsot = resolveAgentHotelMultiRoomLegCustomerFacingSsot(
      pkg,
      profilePriceContext
    );
    const priceIls = legSsot.customerFacingRoomPrice;
    const priceDisplay =
      priceIls != null ? formatHotelIlsRoundedDisplay(priceIls) : null;
    return {
      roomIndex: leg.roomIndex,
      roomName: leg.roomName,
      boardRaw,
      boardLabel,
      priceIls,
      priceDisplay,
      line: formatRoomPricingLine({
        roomIndex: leg.roomIndex,
        roomName: leg.roomName,
        boardLabel,
        priceDisplay,
      }),
      trace: {
        supplierRoomPrice: legSsot.supplierRoomPrice,
        roomMarginApplied: legSsot.roomMarginApplied,
        customerFacingRoomPrice: legSsot.customerFacingRoomPrice,
        pricingStatus: legSsot.pricingStatus,
        pipeline: legSsot.pipeline,
      },
    };
  });
}

/**
 * SSOT combo total from order legs — sum(per-room customerFacing), no recursion into order resolver.
 * @returns {number|null}
 */
export function resolveMultiRoomComboCustomerFacingTotalFromOrder(
  order,
  profilePriceContext = null
) {
  if (!order || typeof order !== "object") return null;

  if (isHotelProfileMultiRoomComboPackage(order.selectedRoom, order.selectedHotelPackage, order)) {
    const enrichedPkg = enrichComboPackageWithRoomsForPricing(
      order,
      order.selectedRoom,
      order.selectedHotelPackage
    );
    const profileDisplay = buildAgentHotelProfilePackageMultiRoomPricingDisplay({
      room: order.selectedRoom,
      pkg: enrichedPkg,
      profilePriceContext,
    });
    if (profileDisplay.isMultiRoom && profileDisplay.totalIls != null) {
      return profileDisplay.totalIls;
    }
  }

  const legsModel = getHotelSummaryLegs(order, { t: (k) => k });
  if (!legsModel.isMultiRoom || legsModel.legs.length < 2) return null;

  const roomRows = buildMultiRoomLegRowsFromOrder(
    order,
    legsModel,
    profilePriceContext
  );
  const allLegsPriced = roomRows.every(
    (row) => row.priceIls != null && Number.isFinite(Number(row.priceIls))
  );
  if (!allLegsPriced) return null;

  return finalizeMultiRoomDisplayFromRoomRows(roomRows, profilePriceContext).totalIls;
}

/**
 * @param {{
 *   currentHotelOrder: object|null,
 *   profilePriceContext?: object|null,
 * }} input
 */
export function buildAgentHotelMultiRoomPricingDisplay({
  currentHotelOrder,
  profilePriceContext = null,
}) {
  const bookingSsotIlsFromOrder = resolveStoredOrderBookingSsotIls(currentHotelOrder);

  const empty = {
    isMultiRoom: false,
    roomRows: [],
    showPerRoomBoard: false,
    sharedBoardLabel: null,
    totalIls: bookingSsotIlsFromOrder,
    totalDisplay: null,
    totalLine: null,
    source: null,
    pipeline: null,
    trace: null,
  };

  const legsModel = getHotelSummaryLegs(currentHotelOrder, { t: (k) => k });
  if (!legsModel.isMultiRoom || legsModel.legs.length < 2) {
    const profileDisplay = resolveProfileComboMultiRoomDisplay(
      currentHotelOrder,
      profilePriceContext
    );
    if (
      profileDisplay?.isMultiRoom &&
      Array.isArray(profileDisplay.roomRows) &&
      profileDisplay.roomRows.length >= 2
    ) {
      return profileDisplay;
    }

    if (resolveSearchOfferRoomsCount(currentHotelOrder) >= 2) {
      const splitDisplay = buildSearchOfferBookingSsotSplitRoomRows(
        currentHotelOrder,
        profilePriceContext
      );
      if (
        splitDisplay?.isMultiRoom &&
        Array.isArray(splitDisplay.roomRows) &&
        splitDisplay.roomRows.length >= 2
      ) {
        return {
          ...splitDisplay,
          showPerRoomPricing: true,
        };
      }
    }

    const totalIls = empty.totalIls;
    const totalDisplay =
      totalIls != null ? formatHotelIlsRoundedDisplay(totalIls) : null;
    return {
      ...empty,
      totalDisplay,
      totalLine: totalDisplay ? `סה״כ לתשלום: ${totalDisplay}` : null,
    };
  }

  const roomRows = buildMultiRoomLegRowsFromOrder(
    currentHotelOrder,
    legsModel,
    profilePriceContext
  );

  return {
    ...finalizeMultiRoomDisplayFromRoomRows(roomRows, profilePriceContext, {
      bookingSsotIlsFromOrder,
    }),
    source: legsModel.source,
  };
}
